import React, { useEffect, useState } from 'react';
import {
  StoreSetupDraft,
  StoreSetupIncluded
} from '../../types/storeSetup.types';
import SetupBoard from './SetupBoard';
import SetupFlow from './SetupFlow';
import SetupOrders from './SetupOrders';
import { SetupBeat } from './setupDraft';
import { SetupTab, setupTabFor } from './setupPreviewModel';
import './SetupPreview.scss';

type Contact = {
  description: string;
  address: string;
  phone: string;
  email: string;
};

type ContactErrors = {
  phone: string;
  email: string;
};

type Props = {
  name: string;
  setup: StoreSetupDraft;
  shown: string[];
  beat: SetupBeat | null;
  included: StoreSetupIncluded;
  contact: Contact;
  contactErrors: ContactErrors;
  onContact: (field: keyof Contact, value: string) => void;
};

function visible<T extends { id: string }>(items: T[], shown: string[]): T[] {
  const ids = new Set(shown);
  return items.filter((item) => ids.has(item.id));
}

const TABS: Array<{ id: SetupTab; label: string }> = [
  { id: 'board', label: 'Tableau' },
  { id: 'orders', label: 'Commandes' }
];

const SetupPreview: React.FC<Props> = ({
  name,
  setup,
  shown,
  beat,
  included,
  contact,
  contactErrors,
  onContact
}) => {
  const [tab, setTab] = useState<SetupTab>('orders');
  const statuses = visible(setup.statuses, shown);
  const searches = visible(setup.searches, shown);
  const charts = visible(setup.charts, shown);
  const automations = visible(setup.automations, shown);
  const fields = visible(setup.fields, shown);
  const live = beat?.phase === 'think' || beat?.phase === 'place';
  const showFlow = automations.length > 0;

  useEffect(() => {
    const next = setupTabFor(setup, beat?.itemId || null);
    if (next) setTab(next);
  }, [beat?.itemId, setup]);

  useEffect(() => {
    if (!showFlow && tab === 'flow') setTab('board');
  }, [showFlow, tab]);

  useEffect(() => {
    if (!beat?.itemId) return;
    const node = document.querySelector(`[data-setup-id="${beat.itemId}"]`);
    node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [beat, tab]);

  return (
    <div className="store-setup__stage">
      <div className={`setup-app${live ? ' is-live' : ''}`} aria-busy={live}>
        <header className="setup-app__bar">
          <div>
            <p>Aperçu</p>
            <h2>{name.trim() || 'Nouveau store'}</h2>
          </div>
          {beat ? (
            <p className={`setup-app__trace setup-app__trace--${beat.phase}`}>
              <span aria-hidden="true" />
              {beat.label}
            </p>
          ) : null}
        </header>

        <div className="setup-app__tabs" role="tablist">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
          {showFlow ? (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'flow'}
              onClick={() => setTab('flow')}
            >
              Auto
            </button>
          ) : null}
        </div>

        <div className="setup-app__view" role="tabpanel">
          {tab === 'board' ? <SetupBoard searches={searches} charts={charts} beat={beat} /> : null}
          {tab === 'orders' ? (
            <SetupOrders statuses={statuses} searches={searches} fields={fields} beat={beat} />
          ) : null}
          {tab === 'flow' ? <SetupFlow automations={automations} beat={beat} /> : null}
        </div>
      </div>

      <details className="store-setup__contact">
        <summary>Coordonnées</summary>
        <p className="store-setup__roles">Rôles · {included.roles.join(' · ')}</p>
        <label>
          Description
          <textarea
            rows={2}
            value={contact.description}
            onChange={(event) => onContact('description', event.target.value)}
          />
        </label>
        <label>
          Adresse
          <input
            value={contact.address}
            onChange={(event) => onContact('address', event.target.value)}
          />
        </label>
        <label>
          Téléphone
          <input
            value={contact.phone}
            inputMode="numeric"
            onChange={(event) => onContact('phone', event.target.value)}
          />
          {contactErrors.phone ? <small>{contactErrors.phone}</small> : null}
        </label>
        <label>
          Email
          <input
            type="email"
            value={contact.email}
            onChange={(event) => onContact('email', event.target.value)}
          />
          {contactErrors.email ? <small>{contactErrors.email}</small> : null}
        </label>
      </details>
    </div>
  );
};

export default SetupPreview;
